/**
 * Semantic diagnostics for the layer Configuration editor: checks that a
 * JSON Schema cannot express, verified against the live map service.
 *
 * - Is the service `url` reachable, and does it answer without an error?
 * - Do the field names referenced by `renderer`, `outFields`, `orderBy` and
 *   `popupTemplate.fieldInfos` actually exist on the service?
 *
 * All results are advisory — they never block saving.
 */

import {
  fetchArcgisServiceInfo,
  isArcgisRestUrl,
} from "@/v2-backoffice-client/layer/service/layer.service.metadata"

export type LayerDiagnosticSeverity = "error" | "warning" | "ok"

export type LayerDiagnostic = {
  severity: LayerDiagnosticSeverity
  message: string
}

/**
 * Feature counts per layer-endpoint + WHERE clause, cached for the page's
 * lifetime: a definitionExpression that matches 0 features renders a
 * perfectly "healthy" EMPTY layer (the classic silent failure — schema-valid
 * predicate, data says no), so drafts carrying one are probed against the
 * service's own count query.
 */
const featureCountCache = new Map<string, Promise<number>>()

function countFeatures(layerUrl: string, where: string): Promise<number> {
  const key = `${layerUrl}\u0000${where}`
  const cached = featureCountCache.get(key)
  if (cached) return cached

  const promise = (async () => {
    const [{ default: esriRequest }, auth] = await Promise.all([
      import("@arcgis/core/request"),
      import("@/v2-backoffice-client/layer/update/preview/layer.preview.auth"),
    ])
    await auth.whenLayerAuthReady()
    const response = await esriRequest(`${layerUrl}/query`, {
      query: { f: "json", where, returnCountOnly: "true" },
      responseType: "json",
    })
    const count = (response.data as { count?: number } | null)?.count
    if (typeof count !== "number") {
      throw new Error("the service returned no count")
    }
    return count
  })()

  featureCountCache.set(key, promise)
  // Failed lookups retry on the next edit instead of pinning the failure.
  promise.catch(() => featureCountCache.delete(key))
  return promise
}

/**
 * Common REST-JSON spellings and their SDK autocast equivalents, for
 * actionable messages. The full translation table lives server-side in
 * v2-arcgis-go (the AI suggestions are auto-normalized with it); this
 * client-side detector only needs to FLAG hand-typed drafts.
 */
const REST_FLAVOR_FIXES: Record<string, string> = {
  classBreaks: "class-breaks",
  uniqueValue: "unique-value",
  dotDensity: "dot-density",
  pieChart: "pie-chart",
  vectorField: "vector-field",
  flowRenderer: "flow",
  colorInfo: "color",
  sizeInfo: "size",
  rotationInfo: "rotation",
  transparencyInfo: "opacity",
  simpleMarker: "simple-marker",
  simpleLine: "simple-line",
  simpleFill: "simple-fill",
  pictureMarker: "picture-marker",
  pictureFill: "picture-fill",
}

/** The enum-carrying keys where the two dialects diverge. */
const REST_FLAVOR_KEYS = new Set([
  "type",
  "style",
  "labelPlacement",
  "classificationMethod",
  "normalizationType",
])

/**
 * Flags REST/webmap-flavor enum spellings, recursively. The SDK silently
 * rejects them (Accessor autocast error in the console; the renderer or
 * symbol just disappears), so drafts carrying them are broken in a way the
 * JSON schema cannot express. Every `esri*`-prefixed enum is REST flavor by
 * construction; the camelCase renderer/visual-variable names are matched
 * explicitly.
 */
function collectRestFlavorTypes(node: unknown, found: LayerDiagnostic[]) {
  if (Array.isArray(node)) {
    for (const child of node) collectRestFlavorTypes(child, found)
    return
  }
  if (!node || typeof node !== "object") return

  const record = node as Record<string, unknown>
  for (const key of REST_FLAVOR_KEYS) {
    const value = record[key]
    if (typeof value !== "string") continue
    const knownFix = REST_FLAVOR_FIXES[value]
    if (knownFix) {
      found.push({
        severity: "error",
        message: `"${key}": "${value}" is the REST flavor — the SDK expects "${knownFix}" and silently rejects this one.`,
      })
    } else if (/^esri[A-Z]/.test(value)) {
      found.push({
        severity: "error",
        message: `"${key}": "${value}" is the REST/webmap flavor — the SDK expects the kebab-case autocast spelling and silently rejects this one.`,
      })
    }
  }
  for (const child of Object.values(record)) {
    collectRestFlavorTypes(child, found)
  }
}

/** Collects every field name the configuration references, with its origin. */
function collectReferencedFields(configuration: Record<string, unknown>) {
  const references: Array<{ field: string; origin: string }> = []

  function add(field: unknown, origin: string) {
    if (typeof field === "string" && field && field !== "*") {
      references.push({ field, origin })
    }
  }

  const renderer = configuration.renderer as Record<string, unknown> | undefined
  if (renderer) {
    add(renderer.field, "renderer.field")
    add(renderer.field2, "renderer.field2")
    add(renderer.field3, "renderer.field3")
    if (Array.isArray(renderer.visualVariables)) {
      for (const visualVariable of renderer.visualVariables) {
        add(
          (visualVariable as Record<string, unknown>)?.field,
          "renderer.visualVariables[].field",
        )
      }
    }
  }

  if (Array.isArray(configuration.outFields)) {
    for (const outField of configuration.outFields) add(outField, "outFields")
  }

  if (Array.isArray(configuration.orderBy)) {
    for (const orderBy of configuration.orderBy) {
      add((orderBy as Record<string, unknown>)?.field, "orderBy[].field")
    }
  }

  const popupTemplate = configuration.popupTemplate as
    | Record<string, unknown>
    | undefined
  if (Array.isArray(popupTemplate?.fieldInfos)) {
    for (const fieldInfo of popupTemplate.fieldInfos) {
      add(
        (fieldInfo as Record<string, unknown>)?.fieldName,
        "popupTemplate.fieldInfos[].fieldName",
      )
    }
  }

  return references
}

export async function runConfigurationDiagnostics(
  draft: string,
): Promise<LayerDiagnostic[]> {
  let configuration: unknown
  try {
    configuration = JSON.parse(draft)
  } catch {
    // Monaco already squiggles parse errors; nothing semantic to add.
    return []
  }
  if (
    typeof configuration !== "object" ||
    configuration === null ||
    Array.isArray(configuration)
  ) {
    return []
  }

  const config = configuration as Record<string, unknown>

  // REST-flavor type spellings are silently rejected by the SDK's autocast
  // (the renderer just disappears), so they rank as errors even though the
  // JSON schema can't catch them.
  const flavorFindings: LayerDiagnostic[] = []
  collectRestFlavorTypes(config, flavorFindings)

  const url = config.url
  if (typeof url !== "string" || !url) return flavorFindings

  const layerId = config.layerId
  const queryUrl =
    isArcgisRestUrl(url) &&
    typeof layerId === "number" &&
    !/\/\d+\/?$/.test(url)
      ? `${url.replace(/\/$/, "")}/${layerId}`
      : url

  if (!isArcgisRestUrl(queryUrl)) {
    // Non-ArcGIS endpoints (WMS, OGC API) have no uniform metadata endpoint
    // that is safe to poke cross-origin — skip quietly.
    return flavorFindings
  }

  let metadata: Awaited<ReturnType<typeof fetchArcgisServiceInfo>>
  try {
    metadata = await fetchArcgisServiceInfo(queryUrl)
  } catch (requestError) {
    // esriRequest rejects with an esri Error (not a native Error) both
    // for transport failures and for ArcGIS `error` payloads.
    const failure = requestError as { name?: string; message?: string } | null
    if (failure?.name === "request:server") {
      return [
        ...flavorFindings,
        {
          severity: "error",
          message: `The service responded with an error: ${failure.message ?? "unknown error"} (${queryUrl})`,
        },
      ]
    }
    return [
      ...flavorFindings,
      {
        severity: "warning",
        message: `Could not reach the service at ${queryUrl}${failure?.message ? ` — ${failure.message}` : " (network or CORS)"}. The layer may still work in the console.`,
      },
    ]
  }

  if (metadata.error) {
    return [
      ...flavorFindings,
      {
        severity: "error",
        message: `The service responded with an error: ${metadata.error.message ?? "unknown error"} (${queryUrl})`,
      },
    ]
  }

  const diagnostics: LayerDiagnostic[] = [...flavorFindings]

  const serviceFields = new Set(
    (metadata.fields ?? [])
      .map((field) => field.name?.toLowerCase())
      .filter(Boolean),
  )

  if (serviceFields.size > 0) {
    for (const { field, origin } of collectReferencedFields(config)) {
      if (!serviceFields.has(field.toLowerCase())) {
        diagnostics.push({
          severity: "warning",
          message: `Field "${field}" (${origin}) does not exist on the service.`,
        })
      }
    }
  }

  // A definitionExpression is validated against the DATA, not just the
  // schema: a predicate on a field that exists but never matches (a quality
  // score that is 0 everywhere, say) renders a healthy-looking empty layer.
  const definitionExpression = config.definitionExpression
  let expressionCount: number | null = null
  if (
    typeof definitionExpression === "string" &&
    definitionExpression.trim() &&
    /\/\d+$/.test(queryUrl.replace(/\/$/, ""))
  ) {
    try {
      expressionCount = await countFeatures(
        queryUrl.replace(/\/$/, ""),
        definitionExpression,
      )
      if (expressionCount === 0) {
        diagnostics.push({
          severity: "error",
          message: `The definitionExpression "${definitionExpression}" matches 0 features — the layer will render empty. Loosen or remove it.`,
        })
      }
    } catch (countError) {
      const failure = countError as { message?: string } | null
      diagnostics.push({
        severity: "warning",
        message: `The service could not evaluate the definitionExpression "${definitionExpression}"${failure?.message ? ` — ${failure.message}` : ""}.`,
      })
    }
  }

  if (diagnostics.length === 0) {
    diagnostics.push({
      severity: "ok",
      message: `Service is reachable${metadata.name ? ` (${metadata.name})` : ""} and all referenced fields exist${
        expressionCount != null
          ? `; the definitionExpression matches ${expressionCount.toLocaleString("en-GB")} features`
          : ""
      }.`,
    })
  }

  return diagnostics
}
